import React, { useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { AuthorisedUser, CollateralAllocationDetail, DealDetail, SpreadsheetMappingType } from 'api'
import { ModalConsumerProps, withModal } from '../modal'

import '../upload/model'
import { Locale, Translation } from 'locale'
import { SpreadsheetParseCompleteHandler, SpreadsheetParseDataHandler, SpreadsheetParseDialog } from '../upload/SpreadsheetParseDialog'

import { isEmpty, noop } from 'lodash'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import {
  AllocationData,
  CollateralAllocationParseResult,
  parseCollateralAllocationSpreadsheet
} from './collateralBasketAllocationUploadParse'
import { isNotEmpty } from 'utils/predicate'
import { SpreadsheetParseValidationResult } from '../allocationDialogs/fundAssignmentDialog/upload/validateTradingEntityAssignmentParseResult'
import { CollateralBasketAllocationValidationPanel } from './CollateralBasketAllocationValidationPanel'
import { CollateralAllocationValidationResult, validateCollateralAllocationParseResult } from './collateralBasketAllocationUploadValidator'
import { CollateralBasketAllocationMappingDialog } from './CollateralBasketAllocationMappingDialog'

export interface CollateralBasketAllocationUploadProps extends ModalConsumerProps {
  initialFile?: File
  initialClipboard?: string
  allocationDetails: CollateralAllocationDetail[]
  authorisedUser: AuthorisedUser
  dealDetail: DealDetail
  onSaveAllocations: (allocations: AllocationData[]) => void
  onClose?: () => void
}

const mapStateToProps = createStructuredSelector({
  authorisedUser: createAuthorisedUserSelector()
})

const CollateralBasketAllocationUploadDialogComponent: React.FC<CollateralBasketAllocationUploadProps> = ({
  initialClipboard,
  initialFile,
  allocationDetails,
  dealDetail,
  authorisedUser,
  onSaveAllocations,
  onClose = noop
}) => {
  const tradableAllocationDetails = allocationDetails.filter(allocationDetail => allocationDetail.collateralAllocation.canTrade) ?? []
  const [validationResults, setValidationResults] = useState<CollateralAllocationValidationResult[]>([])
  const collateralAllocationParser = parseCollateralAllocationSpreadsheet({ authorisedUser, dealDetail })

  const handleParseData: SpreadsheetParseDataHandler<CollateralAllocationParseResult> = async (rowData, _filename, spreadsheetMapping) => {
    const parseResult = collateralAllocationParser({
      rowData,
      filename: _filename,
      spreadsheetMapping
    })

    setValidationResults(
      isEmpty(parseResult.issues)
        ? validateCollateralAllocationParseResult({
            allocationDetails: tradableAllocationDetails,
            dealDetail
          })(parseResult)
        : []
    )

    return parseResult
  }

  const handleComplete: SpreadsheetParseCompleteHandler<CollateralAllocationParseResult> = async ({
    parseResult: { pendingAllocations }
  }) => {
    onSaveAllocations(pendingAllocations)
    onClose()
  }

  return (
    <SpreadsheetParseDialog<CollateralAllocationParseResult>
      dataTestId="CollateralBasketAllocationDialog"
      clipboard={initialClipboard}
      onParseData={handleParseData}
      getMappingModal={(title, mapping, parseResult) => (
        <CollateralBasketAllocationMappingDialog
          title={title}
          mapping={mapping}
          parseResult={parseResult}
          collateralAllocationParser={collateralAllocationParser}
        />
      )}
      onComplete={handleComplete}
      onClose={onClose}
      canUpload={() => !hasValidationIssues(validationResults)}
      parseResultItemsGetter={parseResult => parseResult.pendingAllocations}
      spreadsheetMappingType={SpreadsheetMappingType.COLLATERAL_BASKET_ALLOCATION}
      title={Locale.translate(Translation.TC_ALLOCATION_UPLOAD)}
      translations={{
        emptyDropzoneNarrative: Translation.SC_UPLOAD_DROPZONE_NARRATIVE,
        parseSuccessMessage: Translation.SC_ALLOCATION_SPREADSHEET_SUCCESS,
        parseErrorMessage: Translation.SC_ALLOCATION_SPREADSHEET_ERROR,
        parseErrorNarrative: Translation.SC_ALLOCATION_UPLOAD_ERROR_NARRATIVE
      }}
      uploadFile={initialFile}
      canDownloadUploadMapping={false}>
      {isNotEmpty(validationResults) ? <CollateralBasketAllocationValidationPanel validationResults={validationResults} /> : null}
    </SpreadsheetParseDialog>
  )
}

export const CollateralBasketAllocationUploadDialog = withModal(connect(mapStateToProps)(CollateralBasketAllocationUploadDialogComponent))

const hasValidationIssues = (validationResults: SpreadsheetParseValidationResult<unknown>[]) =>
  isNotEmpty(validationResults) && isNotEmpty(validationResults.flatMap(r => r.validationIssues))
